import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, tap } from 'rxjs/operators';
import SweetAlert2 from 'sweetalert2';

import { NSMessages } from '@ruby/configs/messages.constants';
import { OrderFillService } from '@ruby/core/http/order-fill/order-fill.service';
import { IOrderFillDateRange, IOrderFillLocation, IOrderFillPattern } from '@ruby/modules/order-fill/model/order-fill.interface';
import { EColors } from '@ruby/shared/enums/colors.enums';
import * as OrderHistoryFiltersActions from '@ruby/shared/inventory-management/order-history-header/store/actions/order-history-filters.actions';
import { IError } from '@ruby/shared/models/request/error.interface';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Injectable()
export class OrderHistoryFiltersEffects {
  loadLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderHistoryFiltersActions.loadLocations),
      exhaustMap((action) => this.orderFillService.getLocations(action.data).pipe(
        map((data: Array<IOrderFillLocation>) => OrderHistoryFiltersActions.loadLocationsSuccess({ data })),
        catchError((error: IError) => of(OrderHistoryFiltersActions.loadLocationsFailure({ error })))
      ))
    ));

  loadLocationsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderHistoryFiltersActions.loadLocationsSuccess),
      tap((action) => {
        if (action.data.length === 0) {
          this.showError();
        }
      })
    ), { dispatch: false }
  );

  loadPatterns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderHistoryFiltersActions.loadPatterns),
      exhaustMap((action) => this.orderFillService.getPatterns(action.data).pipe(
        map((data: Array<IOrderFillPattern>) => OrderHistoryFiltersActions.loadPatternsSuccess({ data })),
        catchError((error: IError) => of(OrderHistoryFiltersActions.loadPatternsFailure({ error })))
      ))
    ));

  loadDateRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderHistoryFiltersActions.loadDateRange),
      exhaustMap((action) => this.orderFillService.getDateRange(action.data).pipe(
        map((data: IOrderFillDateRange) => OrderHistoryFiltersActions.loadDateRangeSuccess({ data })),
        catchError((error: IError) => of(OrderHistoryFiltersActions.loadDateRangeFailure({ error })))
      ))
    ));

  failure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          OrderHistoryFiltersActions.loadLocationsFailure,
          OrderHistoryFiltersActions.loadPatternsFailure,
          OrderHistoryFiltersActions.loadDateRangeFailure
        ),
        tap(action => this.utilsService.showErrorToastr(action.error.title, action.error.message))
      ),
    { dispatch: false }
  );

  constructor(private actions$: Actions, private orderFillService: OrderFillService, private utilsService: UtilsService, private router: Router) {
  }

  private showError(): void {
    SweetAlert2.fire({
      title: NSMessages.orderFillLocationsNotFound,
      html: NSMessages.orderFillLocationsRequired,
      icon: 'error',
      heightAuto: false,
      confirmButtonColor: EColors.GOLD,
      willClose: () => { this.router.navigate(['./home/dashboard']); }
    });
  }
}
